import logoUrl from "$assets/logos/elephant-logo-colour-501px_104px.png";
import { Button } from "$components/button/button";
import Layout from "$components/navigation/Layout";
import { toast } from "$global-providers/toast-provider";
import { useIsOnline } from "$routes/_lib/use-is-online";
import { linkToLogin } from "$routes/user/links";
import { styled, Typography } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  width: 315,
  margin: "auto",
});

const Logo = styled("img")({
  maxHeight: 34,
  alignSelf: "center",
  marginBottom: 32,
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h4.fontSize,
  fontWeight: theme.typography.h4.fontWeight,
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  paddingBottom: theme.spacing(1.5),
}));

const PageContent = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
  fontSize: theme.typography.body2.fontSize,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(3),
}));

const LineBreak = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

export type LogoutVariants = "tokenExpired" | "loggedOut";

export const LoggedOutPage: FC<{ variant: LogoutVariants }> = ({ variant }) => {
  const isOnline = useIsOnline();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const logInAgain = () => {
    if (isOnline) {
      navigate(linkToLogin(), { replace: false });
    } else {
      toast.error(
        formatMessage({
          id: "user.loggedOutPage.logInAgain",
          defaultMessage:
            "You are offline. Please try again when you are online.",
        }),
      );
    }
  };
  return (
    <Layout>
      <Container>
        <Logo src={logoUrl} alt="Elephant Logo" />
        <PageTitle>
          {variant === "tokenExpired" ? (
            <FormattedMessage
              id="user.expiredAuth.title"
              defaultMessage="You have been logged out"
            />
          ) : (
            <FormattedMessage
              id="user.loggedOut.title"
              defaultMessage="You have logged out"
            />
          )}
        </PageTitle>
        <PageContent>
          {variant === "tokenExpired" ? (
            <FormattedMessage
              id="user.expiredAuth.body"
              defaultMessage="Your session expired while you were offline. For security we have logged you out.
            {lineBreak}
            Registrations and consultations you completed offline will sync when you log back in. 
            {lineBreak}
            When you’re back online, click the button below to log in."
              values={{
                lineBreak: <LineBreak />,
              }}
            />
          ) : (
            <FormattedMessage
              id="user.loggedOut.body"
              defaultMessage="You logged out while offline. Registrations and consultations you completed offline will sync when you log back in.
          {lineBreak}
          When you’re back online, click the button below to log in."
              values={{
                lineBreak: <LineBreak />,
              }}
            />
          )}
        </PageContent>
        <Button onClick={logInAgain}>Log in</Button>
      </Container>
    </Layout>
  );
};
